import { humanize } from '../../lib/formatLabels';
import { useMemo } from 'react';
import { useStudentInterests } from '../../api/hooks';
import { EmptyState, Badge, Track, Spinner, ErrorPanel, PagedFooter } from '../../components/ui';
import { FaUserGraduate, FaChartLine } from '../../components/ui/icons';
import type { StudentInterestsResponse } from '../../api/types';
import Box from './components/Box';
import MiniStat from './components/MiniStat';
import { dayMonth, parseDate } from './lib/format';
import { usePagination } from '../../lib/usePagination';

interface Props { id: number; }

/** Whole days from today to a scholarship deadline; null without one. */
export function daysUntil(deadline: string | null, now: Date = new Date()): number | null {
  const d = parseDate(deadline);
  if (!d) return null;
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  return Math.round((midnight(d) - midnight(now)) / 86_400_000);
}

export default function InterestsTab({ id }: Props) {
  const { data, isLoading, isError } = useStudentInterests(id);
  if (isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (isError || !data) return <ErrorPanel />;
  return <InterestsContent data={data} />;
}

function InterestsContent({ data }: { data: StudentInterestsResponse }) {
  // Older payloads may miss the optional lists; treat a missing list as empty.
  const careers = data.predicted_careers ?? [];
  const departments = data.predicted_departments ?? [];
  const extracurriculars = data.extracurriculars ?? [];
  const scholarships = data.scholarship_matches ?? [];
  const schPg = usePagination(scholarships, 10);
  const aspirations = data.target_aspirations ?? { universities: [], departments: [] };

  const interestEntries = useMemo(
    () => Object.entries(data.interest_scores ?? {}).sort((a, b) => b[1] - a[1]),
    [data.interest_scores],
  );
  const interestMax = Math.max(1, ...interestEntries.map(([, v]) => v));

  const closingThisMonth = scholarships.filter((m) => {
    const d = parseDate(m.deadline);
    const now = new Date();
    return !!d && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && (daysUntil(m.deadline) ?? -1) >= 0;
  }).length;

  return (
    <div className="space-y-4">
      {data.career_confidence != null && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <MiniStat label="Career confidence" help="career_confidence" value={`${Math.round(data.career_confidence * 100)}%`} />
          <MiniStat label="Predicted careers" help="predicted_careers" value={careers.length} />
          <MiniStat label="Scholarship matches" help="scholarship_matches" value={scholarships.length}
            sub={closingThisMonth > 0 ? `${closingThisMonth} closes this month` : undefined} />
        </div>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Box title="Predicted careers" help="predicted_careers" actions={<span className="text-xs text-muted">fit score</span>}>
          {careers.length === 0 ? (
            <EmptyState compact
              icon={<FaUserGraduate />}
              title="No career predictions yet"
              body="Predictions appear after the student has completed at least a few exams."
            />
          ) : (
            <ul>
              {careers.map((c) => {
                const caption = [c.cluster && humanize(c.cluster), c.stream && humanize(c.stream)].filter(Boolean).join(' · ');
                return (
                  <li key={c.key} className="py-1.5">
                    <div className="grid grid-cols-[120px_minmax(0,1fr)_40px] items-center gap-2.5 text-[13px]">
                      <span className="font-semibold text-ink">{c.label_en ?? c.key}</span>
                      <Track value={c.score} label={`Fit score for ${c.label_en ?? c.key}`} />
                      <span className="text-right font-mono font-medium text-ink">{Math.round(c.score)}%</span>
                    </div>
                    {(caption || c.typical_entrance_score_range) && (
                      <p className="mt-0.5 text-xs text-muted">
                        {caption}{caption && c.typical_entrance_score_range && ' · '}
                        {c.typical_entrance_score_range && <>entrance score: {c.typical_entrance_score_range}</>}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {departments.length > 0 && (
            <>
              <p className="mb-2 mt-4 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Fitting departments</p>
              <div className="flex flex-wrap gap-1.5">
                {departments.slice(0, 5).map((d) => (
                  <Badge key={d} tone="info">{d}</Badge>
                ))}
              </div>
            </>
          )}
        </Box>

        <Box title="Interest scores" help="interest_scores" actions={<span className="text-xs text-muted">RIASEC</span>}>
          {interestEntries.length === 0 ? (
            <EmptyState compact
              icon={<FaChartLine />}
              title="Career compass quiz not completed"
              body="Interest scores appear after the student completes the compass quiz."
            />
          ) : (
            <ul>
              {interestEntries.map(([key, v]) => (
                <li key={key} className="grid grid-cols-[minmax(0,90px)_minmax(0,1fr)_30px] items-center gap-2.5 py-1.5 text-[13px]">
                  <span className="truncate text-ink">{humanize(key)}</span>
                  <Track value={(v / interestMax) * 100} label={humanize(key)} />
                  <span className="text-right font-mono font-medium text-ink">{Math.round(v)}</span>
                </li>
              ))}
            </ul>
          )}
        </Box>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Box title="Target aspirations" help="The universities and departments the student says they are aiming for.">
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-[9px] text-[13px]">
            <dt className="text-muted">Universities</dt>
            <dd className="font-medium text-ink">{aspirations.universities.join(', ') || '—'}</dd>
            <dt className="text-muted">Departments</dt>
            <dd className="font-medium text-ink">{aspirations.departments.join(', ') || '—'}</dd>
            <dt className="text-muted">Extracurriculars</dt>
            <dd className="font-medium text-ink">{extracurriculars.join(', ') || '—'}</dd>
          </dl>
        </Box>

        <Box title="Scholarship matches" help="scholarship_matches"
          actions={scholarships.length > 0 ? <span className="text-xs text-muted">{scholarships.length} open</span> : undefined}>
          {scholarships.length === 0 ? (
            <p className="text-sm text-muted">No matching scholarships yet</p>
          ) : (
            <ul>
              {schPg.pageItems.map((m, i) => {
                const left = daysUntil(m.deadline);
                return (
                  <li key={m.id} className="flex items-center gap-3 border-b border-hairline py-[11px] first:pt-0 last:border-b-0">
                    <span aria-hidden="true" className="grid h-9 w-9 flex-none place-items-center rounded-[12px] bg-mist text-xs font-semibold text-slate">S{schPg.from + i}</span>
                    <span className="min-w-0 flex-1">
                      <a href={m.application_url} target="_blank" rel="noreferrer"
                        className="block font-semibold text-ink underline-offset-2 hover:underline">{m.name}</a>
                      <span className="block text-xs text-muted">
                        {m.provider} · {m.deadline ? `deadline ${dayMonth(m.deadline)}` : 'no deadline'}
                      </span>
                      {m.reasons.length > 0 && <span className="block text-xs text-muted">{m.reasons.join(' · ')}</span>}
                    </span>
                    {m.is_need_based && <Badge tone="info" size="sm">Need-based</Badge>}
                    {left === null
                      ? <Badge tone="neutral" size="sm">Rolling</Badge>
                      : left < 0 ? <Badge tone="neutral" size="sm">Closed</Badge>
                      : <Badge tone={left <= 30 ? 'warning' : 'neutral'} size="sm">{left} {left === 1 ? 'day' : 'days'}</Badge>}
                  </li>
                );
              })}
            </ul>
          )}
          <PagedFooter className="mt-3.5" label="Scholarships pagination" {...schPg} onPage={schPg.setPage} />
        </Box>
      </div>
    </div>
  );
}
